import type { paths } from '@/lib/api/generated';

export const ANALYTICS_PERIOD_OPTIONS = ['7d', '30d'] as const;
export type AnalyticsPeriod = (typeof ANALYTICS_PERIOD_OPTIONS)[number];

export const ANALYTICS_PERIOD_LABELS: Record<AnalyticsPeriod, string> = {
  '7d': '7d',
  '30d': '30d',
};

type MerchantSummaryOperation = paths['/api/v1/merchant/summary']['get'];
type MerchantAnalyticsOverviewOperation = paths['/api/v1/merchant/analytics']['get'];
type MerchantAnalyticsPipelineOperation = paths['/api/v1/merchant/analytics/pipeline']['get'];
type MerchantAnalyticsInventoryOperation = paths['/api/v1/merchant/analytics/inventory']['get'];
type MerchantAnalyticsEngagementOperation = paths['/api/v1/merchant/analytics/engagement']['get'];
type MerchantStoreReportOperation = paths['/api/v1/merchant/analytics/store-report']['get'];
type MerchantAlertsOperation = paths['/api/v1/merchant/analytics/alerts']['get'];
type MerchantPriceBenchmarkOperation =
  paths['/api/v1/merchant/analytics/price-benchmark']['get'];
type MerchantDemandForecastOperation =
  paths['/api/v1/merchant/analytics/demand-forecast']['get'];
type MerchantDevicesHealthOperation = paths['/api/v1/merchant/devices/health']['get'];
type MerchantAskOperation = paths['/api/v1/merchant/analytics/ask']['post'];

export type AnalyticsSummaryResponse =
  MerchantSummaryOperation['responses']['200']['content']['application/json'];
export type AnalyticsOverviewResponse =
  MerchantAnalyticsOverviewOperation['responses']['200']['content']['application/json'];
export type AnalyticsPipelineResponse =
  MerchantAnalyticsPipelineOperation['responses']['200']['content']['application/json'];
export type AnalyticsInventoryResponse =
  MerchantAnalyticsInventoryOperation['responses']['200']['content']['application/json'];
export type AnalyticsEngagementResponse =
  MerchantAnalyticsEngagementOperation['responses']['200']['content']['application/json'];
export type StoreReportResponse =
  MerchantStoreReportOperation['responses']['200']['content']['application/json'];
export type AlertsResponse =
  MerchantAlertsOperation['responses']['200']['content']['application/json'];
export type AnalyticsAlert = AlertsResponse['alerts'][number];
export type AlertSeverity = AnalyticsAlert['severity'];
export type AlertCode = AnalyticsAlert['code'];
export type PriceBenchmarkResponse =
  MerchantPriceBenchmarkOperation['responses']['200']['content']['application/json'];
export type PriceBenchmarkItem = PriceBenchmarkResponse['items'][number];
export type PriceBenchmarkPosition = PriceBenchmarkItem['position'];
export type DemandForecastResponse =
  MerchantDemandForecastOperation['responses']['200']['content']['application/json'];
export type DemandForecastItem = DemandForecastResponse['items'][number];
export type DemandForecastTrend = DemandForecastItem['trend'];
export type AnalyticsDevicesHealthResponse =
  MerchantDevicesHealthOperation['responses']['200']['content']['application/json'];
export type AskRequest =
  MerchantAskOperation['requestBody']['content']['application/json'];
export type AskResponse =
  MerchantAskOperation['responses']['200']['content']['application/json'];

export const STORE_REPORT_LANGS = ['en', 'fil'] as const;
export type StoreReportLang = (typeof STORE_REPORT_LANGS)[number];

export interface AnalyticsSummaryQueryParams {
  period: AnalyticsPeriod;
  branch_id?: string;
}

export interface AnalyticsOverviewQueryParams {
  period: AnalyticsPeriod;
  compare?: 'prev' | 'none';
  branch_id?: string;
}

export interface AnalyticsPipelineQueryParams {
  period: AnalyticsPeriod;
  branch_id?: string;
}

export interface AnalyticsInventoryQueryParams {
  period: AnalyticsPeriod;
  branch_id?: string;
}

export interface AnalyticsEngagementQueryParams {
  period: AnalyticsPeriod;
}

export interface StoreReportQueryParams {
  period: AnalyticsPeriod;
  branch_id?: string;
  lang?: StoreReportLang;
}

export interface AlertsQueryParams {
  period: AnalyticsPeriod;
  branch_id?: string;
  lang?: StoreReportLang;
}

export interface PriceBenchmarkQueryParams {
  period: AnalyticsPeriod;
  branch_id?: string;
  radius_km?: number;
}

export interface DemandForecastQueryParams {
  period: AnalyticsPeriod;
  branch_id?: string;
}

export interface AnalyticsDevicesHealthQueryParams {
  branch_id?: string;
}
